import React, {createContext, useContext} from 'react'

import {EntitySelectorEntityType, EntitySelectorMode} from "./@const"
import {ENTITY_SELECTOR_MODE_CONFIG, EntitySelectorModeConfig} from "./mode-config"
import {MentionOption} from "../editor/plugins/mentions/option"

// What every level of the selector shares: which host it is standing in, what it
// is scoped to, and where a pick goes.
//
// It exists because all three used to be props handed three and four levels down
// -- shell -> main -> tab -> list -- through components that did nothing with
// them but pass them on, and because the two hosts differed by a handful of
// props that each level had to keep forwarding for the difference to reach the
// bottom. `mode` carries that difference now, and only the levels that act on it
// read it.
export type EntitySelectorContextValue = {
    mode                : EntitySelectorMode
    config              : EntitySelectorModeConfig      // the mode's shell, see `mode-config.ts`

    // Host chrome. Rendered only where `config.hasTitleRow`.
    title?              : string

    // What the tabs are scoped to.
    projectId?          : ID
    userMentions        : MentionOption[]

    onClose?            : {():void}
    onEntitySelected    : {(entity:MentionOption):void}
    onTab               : {():void}                     // cycles the sidebar -- every pane's Tab key

    // "+ New X", offered next to a type's own sub-tabs (task.tsx, meeting.tsx).
    // Absent for a host that only picks (the mention popover).
    onCreateNew?        : {(entityType: EntitySelectorEntityType):void}
}

type Props = {
    mode                : EntitySelectorMode

    title?              : string

    projectId?          : ID
    userMentions        : MentionOption[]

    children            : any

    onClose?            : {():void}
    onEntitySelected    : {(entity:MentionOption):void}
    onTab               : {():void}
    onCreateNew?        : {(entityType: EntitySelectorEntityType):void}
}

const EntitySelectorContext = createContext<EntitySelectorContextValue | null>(null)

// Provided by `EntitySelector` itself, around its whole tree.
//
// The value is rebuilt every render on purpose: `onTab` closes over the tab that
// is showing, so a memo would have to be keyed on it anyway -- and these are the
// same props that already re-rendered the whole subtree whenever they changed.
export function EntitySelectorProvider(props: Props) {

    const value: EntitySelectorContextValue = {
        mode                : props.mode,
        config              : ENTITY_SELECTOR_MODE_CONFIG[props.mode],
        title               : props.title,
        projectId           : props.projectId,
        userMentions        : props.userMentions,
        onClose             : props.onClose,
        onEntitySelected    : props.onEntitySelected,
        onTab               : props.onTab,
        onCreateNew         : props.onCreateNew
    }

    return (
        <EntitySelectorContext.Provider value={value}>
            {props.children}
        </EntitySelectorContext.Provider>
    )
}

// Every consumer is inside `EntitySelector` -- the two pieces it shares with
// outside callers (`search/search-row.tsx`, `search/search-input.tsx`, which the
// task-selector field popover reuses on its own) deliberately take props
// instead, so reusing them cannot land here.
export const useEntitySelectorContext = () => {
    const context = useContext(EntitySelectorContext)

    if (!context) {
        throw new Error("useEntitySelectorContext: rendered outside <EntitySelector>")
    }

    return context
}
